import { useEffect, useRef, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

interface ToastMessage {
  id: number;
  text: string;
  type: 'success' | 'error' | 'info' | 'warning';
}

let _addToast: ((text: string, type?: ToastMessage['type']) => void) | null = null;
let _clearToasts: (() => void) | null = null;

/** Imperative toast trigger — call from anywhere */
export function showToast(text: string, type: ToastMessage['type'] = 'info') {
  _addToast?.(text, type);
}

export function clearToasts() {
  _clearToasts?.();
}

/** Mount inside the routed app shell; ToastContainer itself stays router-agnostic. */
export function ToastRouteClearer() {
  const location = useLocation();
  const pagePathRef = useRef(location.pathname);

  useEffect(() => {
    if (pagePathRef.current === location.pathname) return;
    pagePathRef.current = location.pathname;
    clearToasts();
  }, [location.pathname]);

  return null;
}

let _nextId = 0;

export function ToastContainer() {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const addToast = useCallback((text: string, type: ToastMessage['type'] = 'info') => {
    const id = ++_nextId;
    setToasts((prev) => [...prev, { id, text, type }]);
    // Errors are actionable and can contain multiple lines, so keep them visible
    // until manually dismissed. Other notification durations stay unchanged.
    if (type !== 'error') {
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, 3500);
    }
  }, []);

  const dismissToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  useEffect(() => {
    _addToast = addToast;
    _clearToasts = () => setToasts([]);
    return () => {
      _addToast = null;
      _clearToasts = null;
    };
  }, [addToast]);

  if (toasts.length === 0) return null;

  return (
    <div
      className="toast-container"
      role="region"
      aria-label="Notifications"
      // The global search control occupies the top bar. Keep notifications
      // below it so a persistent error cannot intercept search clicks.
      style={{ top: 'calc(var(--ft-topbar-h, 52px) + var(--space-4))' }}
    >
      {toasts.map((t) => (
        <div
          key={t.id}
          className={`toast toast--${t.type}`}
          role={t.type === 'error' ? 'alert' : 'status'}
          aria-live={t.type === 'error' ? 'assertive' : 'polite'}
        >
          <span className="sr-only">{TOAST_LABELS[t.type]}: </span>
          <span className="toast__message">{t.text}</span>
          <button
            type="button"
            className="toast__dismiss"
            onClick={() => dismissToast(t.id)}
            aria-label="Dismiss notification"
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
}

const TOAST_LABELS: Record<ToastMessage['type'], string> = {
  success: 'Success',
  error: 'Error',
  info: 'Info',
  warning: 'Warning',
};
